import React from 'react';
import { createPortal } from 'react-dom';
import { Typography, theme } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { CORAL, CORAL_SOFT_BG } from '../theme/creamyTokens';

const { Text } = Typography;

export interface ActionSheetItem {
  key: string;
  icon: React.ReactNode;
  label: string;
}

interface ActionSheetProps {
  open: boolean;
  title?: string;
  items: ActionSheetItem[];
  onSelect: (key: string) => void;
  onClose: () => void;
}

/**
 * Full-width "pick one of a few actions" mobile bottom sheet -- same portal +
 * drag-to-dismiss mechanics as ConfirmBottomSheet (see that component's own
 * doc comment for why the portal is load-bearing), but a tappable icon+label
 * row list instead of a confirm/cancel button pair. Introduced to replace
 * AppHeader's mobile "+" antd Dropdown, which read as a small desktop-style
 * anchored popover rather than a native mobile sheet. Icon circles are always
 * CORAL regardless of any ConfigProvider scope this sheet's caller sits under
 * -- it's portaled straight onto <body>, outside that scope, and CORAL is
 * this app's brand color for exactly this kind of primary/add action anyway.
 */
const ActionSheet: React.FC<ActionSheetProps> = ({ open, title, items, onSelect, onClose }) => {
  const { token } = theme.useToken();

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="action-sheet-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.45)' }}
          />
          <motion.div
            key="action-sheet"
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            dragSnapToOrigin
            onDragEnd={(_, info) => {
              if (info.offset.y > 80 || info.velocity.y > 500) onClose();
            }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2101,
              width: '100%',
              maxWidth: 480,
              margin: '0 auto',
              background: token.colorBgContainer,
              borderRadius: '20px 20px 0 0',
              boxShadow: '0 -8px 24px rgba(0,0,0,0.16)',
              padding: '10px 8px calc(12px + env(safe-area-inset-bottom, 0px))',
              touchAction: 'none',
            }}
          >
            {/* Grab handle -- purely a visual affordance for the drag gesture, not itself interactive */}
            <div style={{ width: 36, height: 4, borderRadius: 2, background: token.colorBorder, margin: '0 auto 14px' }} />
            {title && (
              <Text strong style={{ display: 'block', fontSize: 16, padding: '0 12px 10px' }}>
                {title}
              </Text>
            )}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => onSelect(item.key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    width: '100%',
                    border: 'none',
                    background: 'none',
                    padding: '10px 12px',
                    borderRadius: 14,
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: 15,
                    color: token.colorText,
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      flexShrink: 0,
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: CORAL_SOFT_BG,
                      color: CORAL,
                    }}
                  >
                    {item.icon}
                  </span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                </button>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ActionSheet;
